// generateWebSiteJsonLd.js
import { getCoporation } from './SchemaObject.js';
import {
  DEFAULT_LANGUAGE,
  localeConvertor,
  PREFERRED_LOCALES,
} from './i18nCommon.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { getLanguageTranslationKey } from './getCurrentLanguage.js';
import { TranslationNamespace } from './translationNamespace.js';
import { getI18nInstance } from './i18n.js';

/**
 * Generate WebSite JSON-LD for homepage with multi-language support
 * This function is designed for Next.js RRv7 server-side rendering
 *
 * @param {Object} params
 * @param {Object} params.state - Redux state object
 * @param {Object} params.fullUrl - Full URL object with origin
 * @param {string} params.titleKey - Translation key for title (default: 'seo-home-title')
 * @param {string} params.descriptionKey - Translation key for description
 * @returns {Promise<Object>} JSON-LD object with WebSite, WebPage, and BreadcrumbList
 */
export const generateWebSiteJsonLd = async ({
  state,
  fullUrl,
  titleKey = 'seo-home-title',
  descriptionKey = 'seo-explore-description',
}) => {
  // Get i18n instance
  const { i18n } = await getI18nInstance();
  const t = i18n.t;
  const { origin } = fullUrl;

  // Use all available languages if not provided
  // Default to PREFERRED_LOCALES which includes all supported languages
  const supportedLanguages =
    getRemoteConfigData(state, SUPPORTED_LANGUAGES) || PREFERRED_LOCALES;
  const alternateLanguages = supportedLanguages.filter(
    lang => DEFAULT_LANGUAGE !== lang
  );

  // fetch each alternate language translations in SEO group
  const loadLanguagePromises = alternateLanguages.map(async lang => {
    if (!i18n.hasResourceBundle(lang, TranslationNamespace.SEO)) {
      await i18n.reloadResources(lang, TranslationNamespace.SEO);
    }
  });
  await Promise.all(loadLanguagePromises);

  // Helper function to get translated title by language
  const getTitleByLang = lang =>
    t(titleKey, {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });

  // Helper function to get translated description by language
  const getDescriptionByLang = lang =>
    t(descriptionKey, {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });

  const localeToBCP47 = lang =>
    localeConvertor({ locale: lang, isBCP47: true });

  const buildNameValueByLang = (lang, translateFn) => ({
    '@value': translateFn(lang),
    '@language': localeToBCP47(lang),
  });

  const buildAlternateValues = translateFn =>
    alternateLanguages.map(lang => ({
      '@value': translateFn(lang),
      '@language': localeToBCP47(lang),
    }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      getCoporation({ origin }),
      {
        '@type': 'WebSite',
        '@id': `${origin}/#website`,
        url: origin,
        inLanguage: supportedLanguages.map(lang => ({
          '@type': 'Language',
          name: t(getLanguageTranslationKey({ language: lang }), {
            ns: TranslationNamespace.SEO,
          }),
          alternateName: localeToBCP47(lang),
        })),
        name: 'SWAG',
        alternateName: buildAlternateValues(getTitleByLang),
        description: buildNameValueByLang(
          DEFAULT_LANGUAGE,
          getDescriptionByLang
        ),
        alternateDescription: buildAlternateValues(getDescriptionByLang),
        image: {
          '@id': `${origin}/#logo`,
        },
        mainEntity: {
          '@id': `${origin}/#webpage`,
        },
        // publisher prop will lead google not recogonize logo in Corporation
        // publisher: {
        //   '@id': 'https://swag.live/#corporation',
        // },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${origin}/search/all?q={search_term}`,
          },
          'query-input': 'required name=search_term',
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${origin}/#webpage`,
        url: origin,
        name: buildNameValueByLang(DEFAULT_LANGUAGE, getTitleByLang),
        alternateName: buildAlternateValues(getTitleByLang),
        breadcrumb: {
          '@id': `${origin}/#breadcrumb`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${origin}/#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: buildNameValueByLang(DEFAULT_LANGUAGE, getTitleByLang),
            alternateName: buildAlternateValues(getTitleByLang),
            item: {
              '@id': `${origin}/#website`,
            },
          },
        ],
      },
    ],
  };
};

export default generateWebSiteJsonLd;
